import clsx from 'clsx';
import { useState } from 'react';

import { SDSClickAway } from '@repo/sds/SDSClickAway';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSInputLabel } from '@repo/sds/SDSInputLabel';
import { FilterDropdownPanel } from '@repo/ui/components/elements/FilterDropdownPanel/FilterDropdownPanel';
import { SearchBar } from '@repo/ui/components/elements/SearchBar/SearchBar';
import { useCoordinatedDropdown } from '@repo/ui/hooks/use-coordinated-dropdown';
import { useDropdownOpenTransition } from '@repo/ui/hooks/use-dropdown-open-transition';

import type { Country } from '../../constants/countries';
import { CountryButton } from '../CountryButton/CountryButton';
import { CountryList } from '../CountryList/CountryList';

import './CountrySelect.scss';

import { DataTestIds } from '@repo/frontend-shared/data-test-ids';

interface CountrySelectProps {
  className?: string;
  compact?: boolean;
  dataTestId?: string;
  defaultCountry?: string;
  display?: 'callingCode' | 'name';
  isRequired?: boolean;
  label?: string;
  modalClassName?: string;
  onSelectCountry?: (country: Country) => void;
  placeholder?: string;
  placement?: 'bottom' | 'top';
  showFlag?: boolean;
  showTriggerFlag?: boolean;
}

export function CountrySelect({
  className,
  compact = false,
  dataTestId,
  defaultCountry,
  display = 'name',
  isRequired,
  label,
  modalClassName = 'CountrySelect__modal__right-0',
  onSelectCountry,
  placeholder,
  placement = 'bottom',
  showFlag = true,
  showTriggerFlag,
}: CountrySelectProps) {
  const { isOpen: open, toggle: toggleOpen, close: closeDropdown } = useCoordinatedDropdown('country-select');
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry || '');

  const closeAndReset = () => {
    closeDropdown();
    setSearch('');
  };

  // Reset the search whenever the panel closes, including when a
  // coordinator (if this is ever rendered inside one) closes it because a
  // sibling dropdown opened instead - that path doesn't go through
  // closeAndReset() above.
  useDropdownOpenTransition(open, { onClose: () => setSearch('') });

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country.code);
    if (onSelectCountry) {
      onSelectCountry(country);
    }
    closeAndReset();
  };

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
  };

  const resolvedPlaceholder = placeholder ?? (display === 'callingCode' ? '+1' : 'Select country');

  const trigger = (
    <SDSClickAway
      onClickAway={closeAndReset}
      className={clsx('CountrySelect', compact && 'CountrySelect--compact', className)}
    >
      <CountryButton
        compact={compact}
        dataTestId={dataTestId}
        display={display}
        isOpen={open}
        onClick={toggleOpen}
        placeholder={resolvedPlaceholder}
        selectedCountry={selectedCountry}
        showFlag={showTriggerFlag ?? showFlag}
      />
      <FilterDropdownPanel
        isOpen={open}
        className={clsx('CountrySelect__modal', placement === 'top' && 'CountrySelect__modal--top', modalClassName)}
        dataTestId={DataTestIds.COMMON.DROPDOWN}
        header={
          <SearchBar
            inputPlaceholder="Search country..."
            showDropdown={false}
            onSearchChange={handleSearchChange}
            options={[]}
            dataTestId={DataTestIds.ONBOARDING.COUNTRY_SEARCH_INPUT}
          />
        }
      >
        <CountryList
          search={search}
          onSelectCountry={handleSelectCountry}
          selectedCountry={selectedCountry}
          showFlag={showFlag}
        />
      </FilterDropdownPanel>
    </SDSClickAway>
  );

  if (compact) {
    return trigger;
  }

  return (
    <SDSFlexbox fullWidth direction="column" gap="text-medium">
      <SDSInputLabel label={label} isRequired={isRequired} />
      {trigger}
    </SDSFlexbox>
  );
}
